import { useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, FileText } from 'lucide-react';
import {
  byPosition,
  CARD_IMPORT_MAX,
  CSV_FIELD_LABELS,
  hasSourceColumns,
  matchImportTargets,
  parseTextImport,
  type BoardSnapshot,
  type ParsedImport,
} from '@kaba/shared';
import { closeCardImport, importGateFailures, runCardImport, useCardImportDialog, type CardImportGateFailure, type CardImportSource } from '../../lib/cardImport';
import { FilePick, isPlanner, readImportFile, Syntax, type SheetChoice } from '../newboard/ImportPanel';
import { fieldClass } from '../newboard/target';
import { Button, cx, Sheet } from '../ui/ui';

const n = (x: number, one: string, many: string) => `${x.toLocaleString('de-DE')} ${x === 1 ? one : many}`;

/** 0.88: Board-Menü / Spaltenmenü / Kartenfeld → mehrere Karten in DIESES Board übernehmen */
export function CardImportDialog({ snapshot }: { snapshot: BoardSnapshot }) {
  const st = useCardImportDialog();
  const open = !!st && st.boardId === snapshot.board.id;
  const columns = useMemo(() => [...snapshot.columns].sort(byPosition), [snapshot.columns]);
  const [tab, setTab] = useState<'text' | 'file'>('text');
  const [text, setText] = useState('');
  const [columnId, setColumnId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sheets, setSheets] = useState<SheetChoice[] | null>(null);
  const [sheet, setSheet] = useState(0);
  const [fileError, setFileError] = useState<string | null>(null);
  const [useSource, setUseSource] = useState<boolean | null>(null);
  const [map, setMap] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; items?: CardImportGateFailure[]; total?: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab('text');
    setText(st?.text ?? '');
    setColumnId(st?.columnId ?? columns[0]?.id ?? '');
    setFile(null);
    setSheets(null);
    setFileError(null);
    setUseSource(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, st]);

  const textParsed = useMemo(() => (text.trim() ? parseTextImport(text) : null), [text]);
  const parsed: ParsedImport | null = tab === 'text' ? textParsed : (sheets?.[sheet]?.parsed ?? null);
  const source: CardImportSource =
    tab === 'text' ? 'text' : (parsed?.source ?? (parsed && isPlanner(parsed) ? 'planner' : file && /\.xlsx$/i.test(file.name) ? 'xlsx' : 'csv'));
  const sourceCols = parsed ? hasSourceColumns(parsed) : false;
  // Spalten übernehmen: Standard, sobald die Quelle eigene Spalten hat (Überschriften, Status, Bucket)
  const useSourceColumns = sourceCols && (useSource ?? true);
  const targets = useMemo(
    () => (parsed ? matchImportTargets(parsed.plan, snapshot, { columnId, useSourceColumns }) : null),
    [parsed, snapshot, columnId, useSourceColumns],
  );

  // Personen aus der Datei: gleiche Namen/E-Mail-Adressen unter den Board-Mitgliedern vorschlagen
  const people = snapshot.members.filter((m) => !m.guest);
  useEffect(() => {
    const auto: Record<string, string> = {};
    for (const m of parsed?.plan.members ?? []) {
      const k = m.name.trim().toLowerCase();
      const hit = people.find((p) => p.name.trim().toLowerCase() === k || (p.email ?? '').toLowerCase() === k);
      if (hit) auto[m.key] = hit.id;
    }
    setMap(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed?.plan.members, snapshot.members]);

  const count = parsed?.plan.cards.length ?? 0;
  const tooMany = count > CARD_IMPORT_MAX;
  const wip = (targets?.counts ?? [])
    .filter((t) => !t.isNew)
    .map((t) => {
      const col = columns.find((c) => c.id === t.target);
      const now = snapshot.cards.filter((c) => c.columnId === t.target && !c.completedAt).length;
      return col?.wipLimit && now + t.cards > col.wipLimit ? `„${col.title}“ hat danach ${now + t.cards} von ${col.wipLimit} erlaubten Karten (WIP-Limit).` : null;
    })
    .filter((x): x is string => !!x);
  const gated = (targets?.counts ?? []).filter((t) => !t.isNew && columns.find((c) => c.id === t.target)?.entryGate).map((t) => t.title);

  const submit = async () => {
    if (!parsed || !count || tooMany) return;
    setBusy(true);
    setError(null);
    try {
      const memberMap: Record<string, string | null> = {};
      for (const m of parsed.plan.members) memberMap[m.key] = map[m.key] || null;
      await runCardImport({ boardId: snapshot.board.id, plan: parsed.plan, source, columnId, useSourceColumns, memberMap });
      closeCardImport();
    } catch (err) {
      const gates = importGateFailures(err);
      setError({ message: (err as Error).message, items: gates?.items, total: gates?.total });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && closeCardImport()} title="Karten importieren" side="wide" description="Mehrere Karten auf einmal in dieses Board übernehmen">
      <div className="flex flex-col gap-4 p-4">
        <div role="tablist" aria-label="Importieren aus" className="flex gap-1">
          {(
            [
              { id: 'text', label: 'Liste einfügen', icon: <FileText size={16} /> },
              { id: 'file', label: 'Excel / Planner / CSV', icon: <FileSpreadsheet size={16} /> },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cx(
                'inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium sm:min-h-10',
                tab === t.id ? 'bg-accent text-on-accent' : 'bg-black/[0.04] hover:bg-black/[0.07] dark:bg-white/5 dark:hover:bg-white/10',
              )}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'text' ? (
          <div className="grid gap-3 lg:grid-cols-[1fr_16rem]">
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              Aufgaben – eine pro Zeile
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={9}
                spellCheck={false}
                autoFocus
                placeholder={'Getränke bestellen #Einkauf @15.6.\nPlakat gestalten\n  [ ] Format A3\nHelferliste aufstellen'}
                className="rounded-lg border border-[var(--line)] bg-[var(--panel)] p-3 font-mono text-sm leading-relaxed font-normal max-lg:h-40"
              />
            </label>
            {/* Am Handy eingeklappt, damit die Vorschau nicht weit nach unten rutscht */}
            <aside className="hidden rounded-xl bg-[var(--column)] p-3 text-xs leading-relaxed lg:block" aria-label="So schreibst du die Liste">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <Syntax code="- Titel" text="eine Karte je Zeile" />
                <Syntax code="#Label" text="Label (neu, falls es fehlt)" />
                <Syntax code="@3.11." text="Frist, auch mit Uhrzeit" />
                <Syntax code="  [ ] Punkt" text="eingerückt: Checkliste" />
                <Syntax code="  Notiz" text="eingerückt: Beschreibung" />
                <Syntax code="## Spalte" text="Spalte (optional)" />
                <Syntax code="[x] Titel" text="schon erledigt" />
              </dl>
              <p className="mt-2 text-[var(--ink-soft)]">Zeilen aus Excel oder Google Sheets mit Überschriften werden als Tabelle erkannt.</p>
            </aside>
            <details className="rounded-xl bg-[var(--column)] p-3 text-xs leading-relaxed lg:hidden">
              <summary className="cursor-pointer text-sm font-medium">So schreibst du die Liste</summary>
              <div className="mt-2">
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <Syntax code="- Titel" text="eine Karte je Zeile" />
                  <Syntax code="#Label" text="Label (neu, falls es fehlt)" />
                  <Syntax code="@3.11." text="Frist, auch mit Uhrzeit" />
                  <Syntax code="  [ ] Punkt" text="eingerückt: Checkliste" />
                  <Syntax code="  Notiz" text="eingerückt: Beschreibung" />
                  <Syntax code="## Spalte" text="Spalte (optional)" />
                  <Syntax code="[x] Titel" text="schon erledigt" />
                </dl>
                <p className="mt-2 text-[var(--ink-soft)]">Zeilen aus Excel oder Google Sheets mit Überschriften werden als Tabelle erkannt.</p>
              </div>
            </details>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <FilePick
              accept=".xlsx,.csv,.tsv,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              label="Excel- oder CSV-Datei auswählen"
              hint="Planner, Asana, Jira, Todoist, TickTick oder eigene Tabelle – oder hierher ziehen"
              file={file}
              onFile={async (f) => {
                setFile(f);
                setSheets(null);
                setSheet(0);
                setFileError(null);
                setUseSource(null);
                if (!f) return;
                const r = await readImportFile(f);
                if ('error' in r) setFileError(r.error);
                else {
                  setSheets(r.sheets);
                  setSheet(r.best);
                }
              }}
            />
            {fileError && (
              <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
                {fileError}
              </p>
            )}
            {sheets && sheets.length > 1 && (
              <label className="flex flex-col gap-1.5 text-sm font-medium">
                Tabellenblatt
                <select className={fieldClass} value={sheet} onChange={(e) => setSheet(Number(e.target.value))}>
                  {sheets.map((s, i) => (
                    <option key={s.name} value={i}>
                      {s.name} ({n(s.parsed.plan.cards.length, 'Karte', 'Karten')})
                    </option>
                  ))}
                </select>
              </label>
            )}
            {parsed?.mapping?.length ? (
              <div className="flex flex-wrap items-center gap-1.5 text-sm">
                <span className="text-[var(--ink-soft)]">Erkannt:</span>
                {parsed.mapping.map((m) => (
                  <span key={m.field} className="rounded-full bg-accent-soft px-2.5 py-0.5 text-xs">
                    {CSV_FIELD_LABELS[m.field]} ← „{m.header}“
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        )}

        <fieldset className="flex flex-col gap-2">
          <legend className="sr-only">Ziel</legend>
          {sourceCols && (
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1 size-4" checked={useSourceColumns} onChange={(e) => setUseSource(e.target.checked)} />
              <span>
                Spalten aus der Quelle übernehmen
                <span className="block text-xs text-[var(--ink-soft)]">Gleichnamige Spalten werden genutzt, fehlende hinten angelegt.</span>
              </span>
            </label>
          )}
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {useSourceColumns ? 'Spalte für Karten ohne Spaltenangabe' : 'In Spalte'}
            <select className={fieldClass} value={columnId} onChange={(e) => setColumnId(e.target.value)}>
              {columns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        {parsed && targets && (
          <section className="flex flex-col gap-2" aria-live="polite" aria-label="Vorschau">
            <h3 className="text-sm font-semibold">Vorschau</h3>
            {count ? (
              <ul className="flex flex-col gap-1 text-sm" data-testid="import-targets">
                {targets.counts.map((t) => (
                  <li key={t.target} className="flex flex-wrap items-center gap-2">
                    <span className="tabular-nums">{n(t.cards, 'Karte', 'Karten')}</span>→<span className="font-medium">„{t.title}“</span>
                    {t.isNew && <span className="rounded-full bg-accent-soft px-2 py-px text-xs">neue Spalte</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[var(--ink-soft)]">Noch keine Karten erkannt.</p>
            )}
            {targets.newLabels.length > 0 && (
              <p className="text-sm">
                Neue Labels: <span className="text-[var(--ink-soft)]">{targets.newLabels.map((l) => l.name).join(', ')}</span>
              </p>
            )}
            {count > 0 && (
              <ol className="max-h-40 list-inside list-decimal overflow-auto text-sm text-[var(--ink-soft)]">
                {parsed.plan.cards.slice(0, 50).map((c) => (
                  <li key={c.key} className="truncate">
                    {c.title}
                  </li>
                ))}
                {count > 50 && <li className="list-none">… und {n(count - 50, 'weitere', 'weitere')}</li>}
              </ol>
            )}
            {[...parsed.warnings, ...wip].map((w) => (
              <p key={w} className="rounded-lg bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
                {w}
              </p>
            ))}
            {gated.length > 0 && (
              <p className="text-sm text-[var(--ink-soft)]">
                {gated.map((g) => `„${g}“`).join(', ')} {gated.length === 1 ? 'hat eine verbindliche Eingangsregel' : 'haben verbindliche Eingangsregeln'} – die Karten müssen sie erfüllen.
              </p>
            )}
            {tooMany && (
              <p className="rounded-lg bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
                Höchstens {CARD_IMPORT_MAX} Karten auf einmal. Bitte teile die Liste oder lege für größere Bestände ein neues Board per Import an.
              </p>
            )}
          </section>
        )}

        {parsed && parsed.plan.members.length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium">Personen zuordnen</legend>
            <p className="text-xs text-[var(--ink-soft)]">Nur Mitglieder dieses Boards. Ohne Zuordnung steht der Name in der Kartenbeschreibung.</p>
            {parsed.plan.members.map((m) => (
              <label key={m.key} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="min-w-32 flex-1 truncate">{m.name}</span>
                <select aria-label={`${m.name} zuordnen`} className={cx(fieldClass, 'w-auto min-w-48 flex-1')} value={map[m.key] ?? ''} onChange={(e) => setMap((x) => ({ ...x, [m.key]: e.target.value }))}>
                  <option value="">nicht zuordnen</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </fieldset>
        )}

        {error && (
          <div role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
            <p>{error.message}</p>
            {error.items && (
              <ul className="mt-1 ml-5 list-disc">
                {error.items.map((g, i) => (
                  <li key={i}>
                    „{g.title}“ – {g.message}
                  </li>
                ))}
                {(error.total ?? 0) > error.items.length && <li className="list-none">… und {(error.total ?? 0) - error.items.length} weitere</li>}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={closeCardImport}>
            Abbrechen
          </Button>
          <Button type="button" variant="primary" disabled={busy || !count || tooMany} onClick={() => void submit()}>
            {busy ? `Lege ${n(count, 'Karte', 'Karten')} an …` : count ? `${n(count, 'Karte', 'Karten')} anlegen` : 'Karten anlegen'}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
